import { useState, Fragment } from "react";
import {
  DropdownControlled,
  DropdownBodyControlled,
  DropdownButton,
} from "@components";
import { XLg, PlusLg } from "react-bootstrap-icons";
import { formatDate, cn, getDatePreset } from "@utils";
import {
  StringEditor,
  EnumEditor,
  DateEditor,
  NumberEditor,
} from "./components";

const filterEditors = {
  string: StringEditor,
  enum: EnumEditor,
  date: DateEditor,
  number: NumberEditor,
};

// Helper to support both string and {value, label} format for enum options
const getEnumValue = (option) =>
  option != null && typeof option === "object" ? option.value : option;
const getEnumLabel = (option) =>
  option != null && typeof option === "object" ? option.label : option;

const getDisplayValue = (type, value, values, filterPrefix, filterSuffix) => {
  switch (type) {
    case "string":
      return value ? `contains "${value}"` : "All";

    case "date": {
      if (!value) return "All";
      const preset = getDatePreset(value.preset);
      if (preset) return preset.label;
      const fmtDateTime = (v) => {
        if (!v) return "...";
        const [date, time] = v.split("T");
        return time ? `${formatDate(date)} ${time}` : formatDate(date);
      };
      if (value.operator === "before")
        return `before ${fmtDateTime(value.to)}`;
      if (value.operator === "between")
        return `between ${fmtDateTime(value.from)} and ${fmtDateTime(value.to)}`;
      return `${value.operator} ${fmtDateTime(value.from)}`;
    }

    case "number": {
      if (!value) return "All";
      const pre = filterPrefix || "";
      const suf = filterSuffix || "";
      const op =
        value.operator === "between"
          ? "between"
          : value.operator === "greaterThan"
            ? "more than"
            : "less than";
      const fromVal = isNaN(value.from) ? value.to : value.from;
      return (
        `${op} ${pre}${fromVal}${suf}` +
        (value.operator === "between" ? ` and ${pre}${value.to}${suf}` : "")
      );
    }

    case "enum":
      if (value.length === 0) return "All";
      if (value.length === 1) {
        const selected = values?.find((opt) => getEnumValue(opt) === value[0]);
        return selected ? getEnumLabel(selected) : value[0];
      }
      return `${value.length} selected`;

    case "includeExcludeEnum": {
      const chains = Array.isArray(value) ? value : [];
      const totalSelected = chains.reduce(
        (sum, c) => sum + (c.selected?.length || 0),
        0,
      );
      if (totalSelected === 0) return "All";
      return chains
        .filter((c) => c.selected?.length > 0)
        .map((c) => {
          const prefix = c.mode === "exclude" ? "excl. " : "";
          return `${prefix}${c.selected.length}`;
        })
        .join(", ");
    }

    case "RecommendationIds":
      if (!Array.isArray(value) || value.length === 0) return "All";
      return value.length;

    default:
      return null;
  }
};

const getChainDisplayValue = (chain, values) => {
  if (!chain.selected || chain.selected.length === 0) {
    return chain.mode === "exclude" ? "None" : "All";
  }
  if (chain.selected.length === 1) {
    const selected = values?.find(
      (opt) => getEnumValue(opt) === chain.selected[0],
    );
    return selected ? getEnumLabel(selected) : chain.selected[0];
  }
  return `${chain.selected.length} groups`;
};

const IncludeExcludeEnumFilterItem = ({
  onRemove,
  onChange,
  value,
  values,
  title,
  Icon,
  buttonRef,
}) => {
  const chains = Array.isArray(value)
    ? value
    : [{ mode: "include", selected: [] }];
  const [openDropdownIndex, setOpenDropdownIndex] = useState(null);

  const hasBoth =
    chains.some((c) => c.mode === "include") &&
    chains.some((c) => c.mode === "exclude");
  const firstHasSelection = chains[0]?.selected?.length > 0;
  const allSelected =
    firstHasSelection &&
    values &&
    chains[0].selected.length >= values.filter(Boolean).length;
  const otherChain = (i) => chains.find((_, j) => j !== i);

  const updateChain = (index, updates) =>
    onChange(chains.map((c, i) => (i === index ? { ...c, ...updates } : c)));

  const addChain = () => {
    const newMode = chains.some((c) => c.mode === "include")
      ? "exclude"
      : "include";
    onChange([...chains, { mode: newMode, selected: [] }]);
    setOpenDropdownIndex(chains.length);
  };

  const pruneEmptyChain = (index) => {
    if (chains.length > 1 && !chains[index]?.selected?.length) {
      onChange(chains.filter((_, i) => i !== index));
      return true;
    }
    return false;
  };

  const toggleDropdown = (index) => {
    if (openDropdownIndex === index) {
      setOpenDropdownIndex(null);
      pruneEmptyChain(index);
    } else {
      let target = index;
      if (openDropdownIndex !== null) {
        const prev = openDropdownIndex;
        if (pruneEmptyChain(prev) && index > prev) target--;
      }
      setOpenDropdownIndex(target);
    }
  };

  return (
    <div className="flex items-center gap-x-1 py-0.5 px-2 ring-1 ring-inset ring-zinc-300 rounded-md text-sm min-h-[34px] shadow-sm whitespace-nowrap">
      {Icon && <Icon />}
      <span className="font-medium">{title}</span>

      {chains.map((chain, i) => (
        <Fragment key={i}>
          {i > 0 && <span className="text-xs font-semibold uppercase">&</span>}
          <DropdownControlled
            isOpen={openDropdownIndex === i}
            setIsOpen={(open) => !open && toggleDropdown(i)}
          >
            <DropdownButton>
              <button
                ref={i === 0 ? buttonRef : undefined}
                className="font-medium hover:bg-zinc-200 px-1 py-0.5 rounded text-primary"
                onClick={() => toggleDropdown(i)}
              >
                {chain.mode === "exclude" ? "Exclude " : "Include "}
                {getChainDisplayValue(chain, values)}
              </button>
            </DropdownButton>
            <DropdownBodyControlled isOpen={openDropdownIndex === i}>
              <div className="px-3 py-2 border-b border-zinc-200">
                <select
                  className="border rounded px-2 py-1 w-full"
                  value={chain.mode}
                  onChange={(e) => updateChain(i, { mode: e.target.value })}
                >
                  <option
                    value="include"
                    disabled={otherChain(i)?.mode === "include"}
                  >
                    Include
                  </option>
                  <option
                    value="exclude"
                    disabled={otherChain(i)?.mode === "exclude"}
                  >
                    Exclude
                  </option>
                </select>
              </div>
              <EnumEditor
                value={chain.selected}
                values={values}
                disabledValues={otherChain(i)?.selected || []}
                onChange={(selected) => updateChain(i, { selected })}
              />
            </DropdownBodyControlled>
          </DropdownControlled>
        </Fragment>
      ))}

      {!hasBoth && firstHasSelection && !allSelected && (
        <button
          onClick={addChain}
          className="p-0.5 rounded-md flex items-center justify-center border hover:bg-primary-200 hover:border-transparent border-primary-200 text-primary"
          title={`Select Groups to ${chains[0]?.mode === "exclude" ? "Include" : "Exclude"}`}
        >
          <PlusLg className="size-3.5" />
        </button>
      )}

      <div
        onClick={onRemove}
        className="ml-1 p-1.5 cursor-pointer hover:bg-zinc-200 shrink-0 rounded"
      >
        <XLg />
      </div>
    </div>
  );
};

const FilterItem = ({
  type,
  onRemove,
  onChange,
  value,
  values,
  title,
  Icon,
  buttonRef,
  filterPrefix,
  filterSuffix,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const Editor = filterEditors[type];

  return (
    <div className="flex items-center gap-x-1 py-0.5 px-2 ring-1 ring-inset  ring-zinc-300 rounded-md text-sm h-[34px] shadow-sm whitespace-nowrap">
      {Icon && <Icon />}
      <span className="font-medium">{title}</span>
      <DropdownControlled isOpen={isDropdownOpen} setIsOpen={setIsDropdownOpen}>
        <DropdownButton>
          <button
            ref={buttonRef}
            className="text-primary font-medium hover:bg-zinc-200 px-1 py-0.5 rounded"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          >
            {getDisplayValue(type, value, values, filterPrefix, filterSuffix)}
          </button>
        </DropdownButton>
        <DropdownBodyControlled isOpen={isDropdownOpen}>
          {Editor && (
            <Editor
              value={value}
              values={values}
              title={title}
              onChange={onChange}
              onClose={() => setIsDropdownOpen(false)}
              filterPrefix={filterPrefix || filterSuffix}
            />
          )}
        </DropdownBodyControlled>
      </DropdownControlled>
      <div
        onClick={onRemove}
        className="ml-1 p-1.5 cursor-pointer hover:bg-zinc-200 shrink-0 rounded"
      >
        <XLg />
      </div>
    </div>
  );
};

const Filters = ({
  data,
  filters,
  columns,
  managedColumns,
  autoOpenKey,
  newFilterButtonRef,
  onRemoveFilter,
  onUpdateFilter,
}) => {
  return !data || (filters || []).length === 0 ? null : (
    <div className="flex flex-none pb-4 pt-2 px-5 gap-2 w-full">
      {(filters || []).map((filter, i) => {
        const attachRef =
          autoOpenKey === filter.key ? newFilterButtonRef : null;

        // Look up fresh values from the column definition first. `columns` is the full
        // definition list; `managedColumns` only holds the columns currently shown, so a
        // filter on a hidden column (e.g. Status switched off in column customisation) must
        // not drop through to the page-data fallback below and offer only the values on
        // the current page.
        const col =
          (columns || []).find((x) => x.key === filter.key) ||
          managedColumns.find((x) => x.key === filter.key);
        let filterValues = col?.values || filter.values;
        const filterPrefix = col?.filterPrefix;
        const filterSuffix = col?.filterSuffix;

        // Fall back to computing values from data if not available
        if (filter.type === "enum" && !filterValues) {
          filterValues = data
            .reduce((acc, obj) => {
              if (!acc.some((item) => item[filter.key] === obj[filter.key])) {
                acc.push(obj);
              }
              return acc;
            }, [])
            .map((x) => x[filter.key])
            .filter(Boolean);
        }

        const Component =
          filter.type === "includeExcludeEnum"
            ? IncludeExcludeEnumFilterItem
            : FilterItem;

        return (
          <Component
            key={i}
            type={filter.type}
            Icon={filter.icon || null}
            title={filter.title}
            value={filter.value}
            values={filterValues}
            buttonRef={attachRef}
            filterPrefix={filterPrefix}
            filterSuffix={filterSuffix}
            onRemove={() => onRemoveFilter(filter.key)}
            onChange={(value) => onUpdateFilter(filter.key, value)}
          />
        );
      })}
    </div>
  );
};

export default Filters;
